import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Phone, Mail, MapPin, Clock, Send, 
  Facebook, CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import VideoHero from "@/components/VideoHero";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent! We'll get back to you soon.");
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Failed to send message. Please call us instead.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Phone",
      content: "613-362-8180",
      link: "tel:613-362-8180",
      description: "Call or text anytime"
    },
    {
      icon: Mail,
      title: "Email",
      content: "tonymovesit@hotmail.com",
      link: "mailto:tonymovesit@hotmail.com",
      description: "We respond within 24 hours"
    },
    {
      icon: MapPin,
      title: "Service Area",
      content: "Cornwall & SDG, Ontario",
      description: "Local moves & surrounding areas"
    },
    {
      icon: Clock,
      title: "Business Hours",
      content: "7 days a week",
      description: "8:00 AM - 6:00 PM"
    }
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero Section with Video */}
      <VideoHero
        title="Get In Touch"
        subtitle="Have questions about your move? Need a quote? We're here to help make your move stress-free."
      />

      {/* Contact Info Cards */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 text-center"
                data-testid={`contact-info-${index}`}
              >
                <div className="bg-[#0047AB]/10 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  <info.icon className="w-6 h-6 text-[#0047AB]" />
                </div>
                <h3 className="font-bold text-slate-900 mb-1">{info.title}</h3>
                {info.link ? (
                  <a 
                    href={info.link}
                    className="text-[#0047AB] font-semibold hover:underline"
                  >
                    {info.content}
                  </a>
                ) : (
                  <p className="text-slate-900 font-semibold">{info.content}</p>
                )}
                <p className="text-sm text-slate-500 mt-1">{info.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Quick Contact */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
              <h2 className="text-2xl font-bold font-['Barlow_Condensed'] text-slate-900 mb-6 uppercase">
                Send Us a Message
              </h2>
              
              {submitted ? (
                <div className="text-center py-12">
                  <div className="bg-green-50 border border-green-200 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Message Sent!</h3>
                  <p className="text-slate-600 mb-6">
                    Thanks for reaching out. We'll get back to you within 24 hours.
                  </p>
                  <Button 
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ name: "", email: "", phone: "", message: "" });
                    }}
                    variant="outline"
                    className="font-bold"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name">Your Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="mt-2"
                      data-testid="contact-name-input"
                      required
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="john@email.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="mt-2"
                        data-testid="contact-email-input"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="613-555-0123"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="mt-2"
                        data-testid="contact-phone-input"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="message">Your Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Tell us about your moving needs..."
                      value={formData.message}
                      onChange={handleInputChange}
                      className="mt-2"
                      rows={5}
                      data-testid="contact-message-input"
                      required
                    />
                  </div>
                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#0047AB] hover:bg-[#003380] text-white font-bold py-6"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                    <Send className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              )}
            </div>

            {/* Quick Contact */}
            <div>
              <div className="bg-[#0047AB] text-white rounded-2xl p-8 mb-8">
                <h2 className="text-2xl font-bold font-['Barlow_Condensed'] mb-6 uppercase">
                  Prefer to Talk?
                </h2>
                <p className="text-white/90 mb-6">
                  Sometimes it's easier to just pick up the phone. 
                  Call Tony directly for immediate assistance.
                </p>
                <a 
                  href="tel:613-362-8180"
                  className="flex items-center gap-4 bg-white/10 rounded-xl p-4 hover:bg-white/20 transition-colors"
                  data-testid="contact-phone-cta"
                >
                  <div className="bg-white text-[#0047AB] p-3 rounded-lg">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm text-white/70">Call or Text</p>
                    <p className="text-2xl font-bold">613-362-8180</p>
                  </div>
                </a>
              </div>

              {/* Social Links */}
              <div className="bg-slate-50 rounded-2xl p-8">
                <h2 className="text-2xl font-bold font-['Barlow_Condensed'] text-slate-900 mb-6 uppercase">
                  Follow Us
                </h2>
                <p className="text-slate-600 mb-6">
                  Stay connected and see what we're up to on social media.
                </p>
                <div className="flex gap-4">
                  <a 
                    href="https://www.facebook.com/profile.php?id=61573945635262"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-4 hover:border-[#0047AB] transition-colors"
                    data-testid="contact-facebook"
                  >
                    <Facebook className="w-6 h-6 text-[#1877F2]" />
                    <span className="font-semibold text-slate-900">Facebook</span>
                  </a>
                </div>
              </div>

              {/* Hours */}
              <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-8">
                <h2 className="text-2xl font-bold font-['Barlow_Condensed'] text-slate-900 mb-6 uppercase">
                  Business Hours
                </h2>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Monday - Friday</span>
                    <span className="font-semibold text-slate-900">8:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Saturday</span>
                    <span className="font-semibold text-slate-900">8:00 AM - 5:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Sunday</span>
                    <span className="font-semibold text-slate-900">By Appointment</span>
                  </div>
                </div>
                <p className="text-sm text-slate-500 mt-4">
                  *Emergency moves available - call for availability
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
